import React, { useState, useEffect, useCallback } from 'react';
import {
  UserPlus, Mail, Copy, Check, Clock, X, Eye, Send, ChevronLeft,
  AlertTriangle, RefreshCw, CheckCircle, Users,
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import OnboardingDocumentActions from '../common/OnboardingDocumentActions';
import { documentDisplayName } from '../../constants/documentTypes';
import { ORGANIZATION_OPTIONS } from '../../constants/organizations';
import { displayValue, formatBoolean, formatCurrency, formatProfileDate } from '../../utils/userProfile';

const inputCls =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors';

const STATUS_TABS = [
  { key: 'submitted', label: 'Awaiting Review', icon: Clock },
  { key: 'invited', label: 'Invited', icon: Mail },
  { key: 'pending', label: 'With Super Admin', icon: Send },
  { key: 'rejected', label: 'Rejected', icon: X },
];

const STATUS_BADGE = {
  invited: { label: 'Invited', cls: 'bg-amber-100 text-amber-700' },
  submitted: { label: 'Awaiting Review', cls: 'bg-blue-100 text-blue-700' },
  pending: { label: 'With Super Admin', cls: 'bg-indigo-100 text-indigo-700' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};

const CTC_FIELDS = [
  ['ctcBasic', 'Basic salary'],
  ['ctcHRA', 'HRA'],
  ['ctcSpecialAllowance', 'Special allowance'],
  ['ctcTravelAllowance', 'Travel allowance'],
  ['ctcITAllowance', 'IT allowance'],
];

/** Travel and IT allowance are optional - they are often zero. */
const REQUIRED_CTC_FIELDS = new Set(['ctcBasic', 'ctcHRA', 'ctcSpecialAllowance']);

const INITIAL_INVITE = {
  name: '', email: '', employmentType: 'fresher', jobTitle: '', organization: '',
  joinDate: '', ctcBasic: '', ctcHRA: '', ctcSpecialAllowance: '', ctcTravelAllowance: '', ctcITAllowance: '',
  isIntern: false, internshipDays: 90,
};

const REVIEW_SECTIONS = [
  {
    title: 'Personal Details',
    rows: [
      ['Date of Birth', (e) => formatProfileDate(e.dateOfBirth)],
      ['Gender', (e) => displayValue(e.gender)],
      ['Marital Status', (e) => displayValue(e.maritalStatus)],
      ['Blood Group', (e) => displayValue(e.bloodGroup)],
      ['Physically Challenged', (e) => formatBoolean(e.physicalChallenged)],
    ],
  },
  {
    title: 'Contact & Address',
    rows: [
      ['Mobile Number', (e) => displayValue(e.mobileNumber)],
      ['Alternate Number', (e) => displayValue(e.alternateNumber)],
      ['Emergency Name', (e) => displayValue(e.emergencyName)],
      ['Emergency Relation', (e) => displayValue(e.emergencyRelation)],
      ['Emergency Contact', (e) => displayValue(e.emergencyContactNumber)],
      ['Current Address', (e) => displayValue(e.presentAddress)],
      ['Permanent Address', (e) => displayValue(e.permanentAddress)],
      ['City', (e) => displayValue(e.city)],
      ['State', (e) => displayValue(e.state)],
      ['Pin Code', (e) => displayValue(e.pinCode)],
    ],
  },
  {
    title: 'KYC',
    rows: [
      ['PAN Number', (e) => displayValue(e.panNumber)],
      ['Aadhaar Number', (e) => displayValue(e.aadhaarNumber)],
      ['Passport Number', (e) => displayValue(e.passportNumber)],
      ['PF Details', (e) => displayValue(e.pfDetails)],
      ['Medical Insurance', (e) => displayValue(e.medicalInsurance)],
    ],
  },
  {
    title: 'Education',
    rows: [
      ['School', (e) => displayValue(e.schoolName)],
      ['School Board', (e) => displayValue(e.schoolBoard)],
      ['School Year', (e) => displayValue(e.schoolYearOfGraduation)],
      ['School %/CGPA', (e) => displayValue(e.schoolPercentageOrCGPA)],
      ['University', (e) => displayValue(e.universityName)],
      ['University Board', (e) => displayValue(e.universityBoard)],
      ['University Year', (e) => displayValue(e.universityYearOfGraduation)],
      ['University %/CGPA', (e) => displayValue(e.universityPercentageOrCGPA)],
      ['Post Graduation', (e) => displayValue(e.postGraduationName)],
      ['Highest Qualification', (e) => displayValue(e.highestEducationCategory)],
      ['Highest Institution', (e) => displayValue(e.highestEducationInstitutionName)],
      ['Highest Year', (e) => displayValue(e.highestEducationYearOfPassing)],
      ['Highest %/CGPA', (e) => displayValue(e.highestEducationPercentageOrCGPA)],
    ],
  },
  {
    title: 'References',
    rows: [
      ['Reference 1 Name', (e) => displayValue(e.reference1Name)],
      ['Reference 1 Relationship', (e) => displayValue(e.reference1Relationship)],
      ['Reference 1 Mobile', (e) => displayValue(e.reference1Mobile)],
      ['Reference 1 Emergency Contact', (e) => displayValue(e.reference1EmergencyContactNumber)],
      ['Reference 1 Address', (e) => displayValue(e.reference1Address)],
      ['Reference 2 Name', (e) => displayValue(e.reference2Name)],
      ['Reference 2 Relationship', (e) => displayValue(e.reference2Relationship)],
      ['Reference 2 Mobile', (e) => displayValue(e.reference2Mobile)],
      ['Reference 2 Emergency Contact', (e) => displayValue(e.reference2EmergencyContactNumber)],
      ['Reference 2 Address', (e) => displayValue(e.reference2Address)],
    ],
  },
  {
    title: 'Previous Employment',
    rows: [
      ['Total Experience', (e) => displayValue(e.totalExperience)],
      ['Previous Company', (e) => displayValue(e.previousCompanyName)],
      ['Location', (e) => displayValue(e.previousCompanyLocation)],
      ['Previous Job Title', (e) => displayValue(e.previousJobTitle)],
      ['Supervisor', (e) => displayValue(e.previousSupervisorName)],
      ['Supervisor Contact', (e) => displayValue(e.previousSupervisorContact)],
      ['Supervisor Email', (e) => displayValue(e.previousSupervisorEmail)],
      ['Start Date', (e) => formatProfileDate(e.previousStartDate)],
      ['End Date', (e) => formatProfileDate(e.previousEndDate)],
      ['Reason For Leaving', (e) => displayValue(e.reasonForLeaving)],
      ['Relieving Letter Submitted', (e) => formatBoolean(e.relievingLetterSubmitted)],
    ],
  },
  {
    title: 'Acknowledgement',
    rows: [
      ['Acknowledgement Accepted', (e) => formatBoolean(e.acknowledgementAccepted)],
      ['Applicant Signature', (e) => displayValue(e.candidateSignature)],
      ['Declaration Date', (e) => formatProfileDate(e.candidateDeclarationDate)],
    ],
  },
];

const CandidateInvites = ({ onSidebarCountsRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('submitted');
  const [candidates, setCandidates] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState(INITIAL_INVITE);
  const [inviting, setInviting] = useState(false);
  const [issuedCredentials, setIssuedCredentials] = useState(null);
  const [copied, setCopied] = useState(false);
  const [reviewId, setReviewId] = useState(null);

  const fetchCandidates = useCallback(async () => {
    try {
      const res = await api.get(`/onboarding/candidates?status=${filter}`);
      setCandidates(res.data.candidates || []);
    } catch (error) {
      console.error('Fetch candidates error:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const handleInviteChange = (field, value) => setInvite((prev) => ({ ...prev, [field]: value }));

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!invite.name.trim() || !invite.email.trim() || !invite.jobTitle.trim()) {
      toast.error('Name, email and job title are required');
      return;
    }

    setInviting(true);
    try {
      const payload = { ...invite };
      if (payload.isIntern) {
        payload.internshipDays =
          payload.internshipDays === '' || payload.internshipDays == null
            ? 90
            : Number(payload.internshipDays);
      }
      const res = await api.post('/onboarding/invite', payload);
      setIssuedCredentials({ ...res.data.credentials, emailSent: res.data.emailSent, name: invite.name });
      toast[res.data.emailSent ? 'success' : 'warning'](res.data.message);
      setInvite(INITIAL_INVITE);
      setShowInvite(false);
      setFilter('invited');
      if (onSidebarCountsRefresh) onSidebarCountsRefresh();
    } catch (error) {
      const apiErrors = error.response?.data?.errors;
      toast.error(apiErrors?.[0]?.msg || error.response?.data?.message || 'Failed to invite candidate');
    } finally {
      setInviting(false);
    }
  };

  const handleResend = async (candidate) => {
    try {
      const res = await api.post(`/onboarding/invite/${candidate.id || candidate._id}/resend`);
      setIssuedCredentials({ ...res.data.credentials, emailSent: res.data.emailSent, name: candidate.name });
      toast[res.data.emailSent ? 'success' : 'warning'](res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend credentials');
    }
  };

  const copyCredentials = async () => {
    if (!issuedCredentials) return;
    const text = `Login ID: ${issuedCredentials.loginId}\nEmail: ${issuedCredentials.email}\nTemporary password: ${issuedCredentials.tempPassword}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const handleReviewed = () => {
    setReviewId(null);
    fetchCandidates();
    if (onSidebarCountsRefresh) onSidebarCountsRefresh();
  };

  const handleListDocumentChanged = (candidateId, document) => {
    setCandidates((previous) => previous.map((candidate) => {
      const id = candidate.id || candidate._id;
      return String(id) === String(candidateId) ? { ...candidate, onboardingPdf: document } : candidate;
    }));
  };

  if (reviewId) {
    return <CandidateReview candidateId={reviewId} onBack={() => setReviewId(null)} onReviewed={handleReviewed} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Onboarding</h1>
          <p className="text-gray-600">Invite candidates to complete their own onboarding, then review what they submit</p>
        </div>
        <button
          onClick={() => setShowInvite((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
        >
          <UserPlus className="h-4 w-4" /> Invite Candidate
        </button>
      </div>

      {issuedCredentials && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-green-800">
                Temporary credentials for {issuedCredentials.name}
                {issuedCredentials.emailSent ? ' (emailed to the candidate)' : ' — email failed, share these manually'}
              </p>
              <div className="mt-2 text-sm text-green-900 font-mono space-y-0.5">
                <div>Login ID: {issuedCredentials.loginId}</div>
                <div>Email: {issuedCredentials.email}</div>
                <div>Temporary password: {issuedCredentials.tempPassword}</div>
              </div>
              <p className="mt-2 text-xs text-green-700">
                This password is shown only once. The candidate signs in at the normal login page.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={copyCredentials}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-green-300 text-green-800 hover:bg-green-100"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button onClick={() => setIssuedCredentials(null)} className="text-green-600 hover:text-green-800">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvite && (
        <form onSubmit={handleInviteSubmit} className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Invite a Candidate</h2>
            <p className="text-sm text-gray-500 mt-1">
              The candidate receives temporary sign-in credentials by email and fills in their own details and documents.
            </p>
          </div>
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Candidate Name <span className="text-red-500">*</span></label>
                <input type="text" value={invite.name} onChange={(e) => handleInviteChange('name', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Candidate Email <span className="text-red-500">*</span></label>
                <input type="email" value={invite.email} onChange={(e) => handleInviteChange('email', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Candidate Type <span className="text-red-500">*</span></label>
                <select value={invite.employmentType} onChange={(e) => handleInviteChange('employmentType', e.target.value)} className={inputCls}>
                  <option value="fresher">Fresher</option>
                  <option value="experienced">Experienced</option>
                </select>
                <p className="mt-1 text-xs text-gray-400">Decides which documents the candidate is asked to upload</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title <span className="text-red-500">*</span></label>
                <input type="text" value={invite.jobTitle} onChange={(e) => handleInviteChange('jobTitle', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                <select value={invite.organization} onChange={(e) => handleInviteChange('organization', e.target.value)} className={inputCls}>
                  <option value="">Select Organization</option>
                  {ORGANIZATION_OPTIONS.map((org) => <option key={org} value={org}>{org}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Join Date</label>
                <input type="date" value={invite.joinDate} onChange={(e) => handleInviteChange('joinDate', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Internship</label>
                <label className="inline-flex h-[42px] items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!invite.isIntern}
                    onChange={(e) => handleInviteChange('isIntern', e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">This candidate is an intern</span>
                </label>
                <p className="mt-1 text-xs text-gray-400">
                  Interns are exempt from the ₹200 professional tax until HR confirms them.
                </p>
              </div>
              {invite.isIntern && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Internship Days</label>
                  <input
                    type="number" min="0" step="1"
                    value={invite.internshipDays}
                    onChange={(e) => handleInviteChange('internshipDays', e.target.value)}
                    className={inputCls}
                  />
                  <p className="mt-1 text-xs text-gray-400">Counted from the join date. Default 90 days.</p>
                </div>
              )}
            </div>

            {/* <div className="border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-1">Offer / CTC (monthly)</h3>
              <p className="text-xs text-gray-500 mb-3">
                Candidates never see or enter salary details. You can fill these in now or while reviewing their submission.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {CTC_FIELDS.map(([field, label]) => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                    <input
                      type="number" min="0" step="0.01"
                      value={invite[field]}
                      onChange={(e) => handleInviteChange(field, e.target.value)}
                      className={inputCls}
                    />
                  </div>
                ))}
              </div>
            </div> */}
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
            <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={inviting} className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
              <Mail className="h-4 w-4" /> {inviting ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => { setLoading(true); setFilter(key); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
              filter === key ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Onboarding PDF</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {candidates.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                      <Users className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                      No candidates in this stage
                    </td>
                  </tr>
                )}
                {candidates.map((candidate) => {
                  const id = candidate.id || candidate._id;
                  const badge = STATUS_BADGE[candidate.onboardingStatus] || STATUS_BADGE.invited;
                  return (
                    <tr key={id}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{candidate.name}</div>
                        <div className="text-xs text-gray-500">{candidate.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                        {candidate.employmentType || '—'}
                        {candidate.isIntern && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                            Intern
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{candidate.jobTitle || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 font-mono">{candidate.tempEmployeeId || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {candidate.onboardingPdf ? (
                          <OnboardingDocumentActions
                            document={candidate.onboardingPdf}
                            employeeId={id}
                            onChanged={(document) => handleListDocumentChanged(id, document)}
                          />
                        ) : (
                          <span className="text-xs text-gray-400">Not generated</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          {candidate.onboardingStatus === 'invited' && (
                            <button
                              onClick={() => handleResend(candidate)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                              <RefreshCw className="h-3.5 w-3.5" /> Resend
                            </button>
                          )}
                          <button
                            onClick={() => setReviewId(id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700"
                          >
                            <Eye className="h-3.5 w-3.5" /> Review
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/** Read-only view of what the candidate submitted, plus the HR/CTC fields and the decision. */
const CandidateReview = ({ candidateId, onBack, onReviewed }) => {
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [hrForm, setHrForm] = useState({});
  const [acting, setActing] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/onboarding/candidates/${candidateId}`);
        const emp = res.data.employee;
        setEmployee(emp);
        setDocuments(res.data.documents || []);
        setChecklist(res.data.checklist || []);
        setHrForm({
          jobTitle: emp.jobTitle || '',
          organization: emp.organization || '',
          joinDate: emp.joinDate ? new Date(emp.joinDate).toISOString().split('T')[0] : '',
          ctcBasic: emp.ctcBasic ?? '',
          ctcHRA: emp.ctcHRA ?? '',
          ctcSpecialAllowance: emp.ctcSpecialAllowance ?? '',
          ctcTravelAllowance: emp.ctcTravelAllowance ?? '',
          ctcITAllowance: emp.ctcITAllowance ?? '',
          reportingManagerEmployeeId: emp.reportingManager?.employeeId || '',
          isIntern: emp.isIntern === true,
          internshipDays: emp.internshipDays ?? 90,
        });
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load candidate');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [candidateId]);

  const handleDocumentChanged = (updated) => {
    setDocuments((previous) => [updated, ...previous.filter((doc) => doc.documentType !== updated.documentType)]);
  };

  const handleForward = async () => {
    setActing(true);
    try {
      const payload = { ...hrForm };
      if (payload.isIntern) {
        payload.internshipDays =
          payload.internshipDays === '' || payload.internshipDays == null
            ? 90
            : Number(payload.internshipDays);
      }
      await api.put(`/onboarding/candidates/${candidateId}/forward`, payload);
      toast.success('Sent to super admin for approval');
      onReviewed();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send to super admin');
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    setActing(true);
    try {
      await api.put(`/onboarding/candidates/${candidateId}/reject`, { rejectionReason: rejectReason });
      toast.success('Candidate onboarding rejected');
      onReviewed();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject candidate');
    } finally {
      setActing(false);
      setShowReject(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!employee) return null;

  const canDecide = employee.onboardingStatus === 'submitted';
  const badge = STATUS_BADGE[employee.onboardingStatus] || STATUS_BADGE.invited;
  const onboardingPdf = documents.find((doc) => doc.documentType === 'onboarding_form');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button onClick={onBack} className="mt-1 text-gray-400 hover:text-gray-600">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{employee.name}</h1>
            <p className="text-gray-600">
              {employee.email} · <span className="capitalize">{employee.employmentType || 'fresher'}</span> candidate
              {employee.isIntern && (
                <span className="ml-2 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                  Intern
                </span>
              )}
            </p>
          </div>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${badge.cls}`}>
          {badge.label}
        </span>
      </div>

      {!canDecide && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          {employee.onboardingStatus === 'invited'
            ? 'This candidate has not submitted their onboarding form yet.'
            : employee.onboardingStatus === 'pending'
            ? 'This submission has already been sent to the super admin for final approval.'
            : 'This candidate is no longer awaiting your review.'}
          {employee.onboardingRejectionReason && (
            <p className="mt-2"><strong>Rejection reason:</strong> {employee.onboardingRejectionReason}</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-indigo-200 bg-indigo-50 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-indigo-950">Completed Onboarding Form PDF</h2>
          <p className="mt-1 text-sm text-indigo-700">
            {onboardingPdf ? onboardingPdf.originalFileName : 'The completed PDF has not been generated yet.'}
          </p>
        </div>
        {onboardingPdf ? (
          <OnboardingDocumentActions document={onboardingPdf} employeeId={candidateId} onChanged={handleDocumentChanged} />
        ) : (
          <span className="text-sm font-medium text-amber-700">PDF unavailable</span>
        )}
      </div>

      {/* Documents */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Submitted Documents</h2>
        </div>
        <div className="p-6 grid grid-cols-1 gap-3">
          {documents.map((doc) => (
              <div key={doc._id} className="flex flex-wrap items-center justify-between gap-3 border border-gray-100 rounded-lg px-4 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-800 truncate">{documentDisplayName(doc)}</p>
                    <p className="text-xs text-gray-400 truncate">{doc.originalFileName}</p>
                  </div>
                </div>
                <OnboardingDocumentActions document={doc} employeeId={candidateId} onChanged={handleDocumentChanged} />
              </div>
          ))}
          {checklist.filter((item) => !documents.some((doc) => doc.documentType === item.value)).map((item) => (
            <div key={item.value} className="flex items-center gap-2 border border-red-100 rounded-lg px-4 py-3 text-sm text-red-600">
              <X className="h-4 w-4" /> Missing: {item.label}
            </div>
          ))}
        </div>
      </div>

      {/* Candidate-submitted details */}
      {REVIEW_SECTIONS.map((section) => (
        <div key={section.title} className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">{section.title}</h2>
          </div>
          <dl className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            {section.rows.map(([label, getValue]) => (
              <div key={label} className="flex justify-between gap-4 border-b border-gray-50 pb-2">
                <dt className="text-sm text-gray-500">{label}</dt>
                <dd className="text-sm font-medium text-gray-900 text-right break-words">{getValue(employee)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}

      {/* HR-controlled employment details */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Employment & CTC (HR)</h2>
          <p className="text-sm text-gray-500 mt-1">
            These are not visible to the candidate and must be complete before sending to the super admin.
            Total monthly CTC: {formatCurrency(
              CTC_FIELDS.reduce((sum, [field]) => sum + (Number(hrForm[field]) || 0), 0)
            )}
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Title <span className="text-red-500">*</span></label>
              <input type="text" value={hrForm.jobTitle} disabled={!canDecide}
                onChange={(e) => setHrForm((p) => ({ ...p, jobTitle: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization <span className="text-red-500">*</span></label>
              <select value={hrForm.organization} disabled={!canDecide}
                onChange={(e) => setHrForm((p) => ({ ...p, organization: e.target.value }))} className={inputCls}>
                <option value="">Select Organization</option>
                {ORGANIZATION_OPTIONS.map((org) => <option key={org} value={org}>{org}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Join Date</label>
              <input type="date" value={hrForm.joinDate} disabled={!canDecide}
                onChange={(e) => setHrForm((p) => ({ ...p, joinDate: e.target.value }))} className={inputCls} />
            </div>
            {CTC_FIELDS.map(([field, label]) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label} {REQUIRED_CTC_FIELDS.has(field) && <span className="text-red-500">*</span>}
                </label>
                <input type="number" min="0" step="0.01" value={hrForm[field]} disabled={!canDecide}
                  onChange={(e) => setHrForm((p) => ({ ...p, [field]: e.target.value }))} className={inputCls} />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Manager (Employee ID)</label>
              <input type="text" value={hrForm.reportingManagerEmployeeId} disabled={!canDecide}
                onChange={(e) => setHrForm((p) => ({ ...p, reportingManagerEmployeeId: e.target.value.toUpperCase() }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Internship</label>
              <label className="inline-flex h-[42px] items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!hrForm.isIntern}
                  disabled={!canDecide}
                  onChange={(e) => setHrForm((p) => ({ ...p, isIntern: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">Intern</span>
              </label>
              <p className="mt-1 text-xs text-gray-400">Exempt from the ₹200 professional tax until confirmed.</p>
            </div>
            {hrForm.isIntern && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Internship Days</label>
                <input type="number" min="0" step="1" value={hrForm.internshipDays ?? 90} disabled={!canDecide}
                  onChange={(e) => setHrForm((p) => ({ ...p, internshipDays: e.target.value }))} className={inputCls} />
              </div>
            )}
          </div>
        </div>
      </div>

      {canDecide && (
        <div className="flex flex-wrap justify-end gap-3">
          <button
            onClick={() => setShowReject(true)}
            disabled={acting}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            <X className="h-4 w-4" /> Reject
          </button>
          <button
            onClick={handleForward}
            disabled={acting}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            <Send className="h-4 w-4" /> {acting ? 'Sending…' : 'Send to Super Admin for Approval'}
          </button>
        </div>
      )}

      {showReject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Reject {employee.name}?</h3>
                <p className="text-sm text-gray-500 mt-1">The candidate will not be able to sign in again.</p>
              </div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
            <textarea
              rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowReject(false)} className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleReject} disabled={acting} className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                {acting ? 'Rejecting…' : 'Reject Candidate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateInvites;
